import { Head, Link } from "@inertiajs/react"
import { Check } from "lucide-react"

import LandingNavbar from "@/components/landing-navbar"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { signUpPath } from "@/routes"

const features = [
  "Unlimited client management",
  "Comprehensive tax reporting",
  "Performance tracking and analytics",
  "Inflation-adjusted calculations",
  "Export reports in multiple formats",
  "Real-time data updates",
  "Secure data storage",
  "Priority support",
]

export default function Pricing() {
  return (
    <>
      <Head title="Pricing">
        <link rel="preconnect" href="https://fonts.bunny.net" />
        <link
          href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600"
          rel="stylesheet"
        />
      </Head>

      <div className="flex min-h-screen flex-col bg-[#FDFDFC] text-[#1b1b18] dark:bg-[#0a0a0a]">
        <LandingNavbar />

        <main className="flex-1">
          {/* Pricing Header */}
          <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
            <div className="mx-auto max-w-2xl text-center">
              <h1 className="text-4xl font-bold tracking-tight text-neutral-900 sm:text-5xl dark:text-neutral-100">
                Simple, Transparent Pricing
              </h1>
              <p className="mt-4 text-lg text-neutral-600 dark:text-neutral-400">
                Pay only for what you need. No hidden fees, no surprises.
              </p>
            </div>
          </section>

          {/* Pricing Card */}
          <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8 lg:pb-24">
            <div className="mx-auto max-w-2xl">
              <Card className="border-2">
                <CardHeader className="text-center">
                  <CardTitle className="text-2xl">Per Client Pricing</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-neutral-900 dark:text-neutral-100">
                      $5
                    </span>
                    <span className="text-xl text-neutral-600 dark:text-neutral-400">
                      {" "}
                      USD
                    </span>
                  </div>
                  <CardDescription className="mt-2 text-base">
                    per client per month
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                      Everything included:
                    </h3>
                    <ul className="space-y-3">
                      {features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                          <span className="text-neutral-700 dark:text-neutral-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="pt-4">
                    <Link href={signUpPath()} className="block">
                      <Button size="lg" className="w-full">
                        Get Started
                      </Button>
                    </Link>
                    <p className="mt-4 text-center text-sm text-neutral-600 dark:text-neutral-400">
                      No credit card required to start. Cancel anytime.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="bg-neutral-50 dark:bg-neutral-900">
            <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
              <div className="mx-auto max-w-2xl">
                <h2 className="text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl dark:text-neutral-100">
                  Frequently Asked Questions
                </h2>
                <dl className="mt-10 space-y-8">
                  <div>
                    <dt className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                      How does billing work?
                    </dt>
                    <dd className="mt-2 text-base text-neutral-600 dark:text-neutral-400">
                      You're billed monthly based on the number of active clients in your account.
                      Add or remove clients anytime, and your next bill will reflect the changes.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                      Can I change my plan later?
                    </dt>
                    <dd className="mt-2 text-base text-neutral-600 dark:text-neutral-400">
                      Yes, you can add or remove clients at any time. Your billing will adjust
                      automatically on your next billing cycle.
                    </dd>
                  </div>
                  <div>
                    <dt className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
                      Is there a free trial?
                    </dt>
                    <dd className="mt-2 text-base text-neutral-600 dark:text-neutral-400">
                      Yes, you can start using the platform immediately. No credit card required to
                      get started.
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  )
}


